/**
 * CSV serialisation.
 *
 * Two separate concerns, deliberately not conflated:
 *
 *  1. *Quoting* — commas, quotes, newlines and carriage returns inside a field.
 *     RFC 4180: wrap in double quotes, double any embedded quote.
 *
 *  2. *Formula injection* — a text cell beginning with `=`, `+`, `-`, `@`, tab
 *     or CR is executed as a formula by Excel/Sheets/LibreOffice. The mitigation
 *     is a leading apostrophe, but it is applied **only to strings**. Numeric
 *     cells are written as bare numbers, so a legitimate -5 stays the number -5
 *     and does not become the text "'-5".
 */
const NEEDS_QUOTING = /[",\r\n]/;
const FORMULA_LEAD = /^[=+\-@\t\r]/;
/** Escapes one already-stringified field for CSV output. */
export function escapeCsvField(value) {
    if (!NEEDS_QUOTING.test(value))
        return value;
    return `"${value.replaceAll('"', '""')}"`;
}
/**
 * Converts a cell to its CSV representation.
 *
 * Numbers and booleans are written bare. Strings are checked for a formula
 * lead character and prefixed with `'` when found, which every major
 * spreadsheet reads as "treat the rest as literal text".
 */
export function csvCell(value) {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : '';
    }
    if (typeof value === 'boolean')
        return value ? 'true' : 'false';
    const text = String(value);
    const safe = FORMULA_LEAD.test(text) ? `'${text}` : text;
    return escapeCsvField(safe);
}
export function csvRow(values) {
    return values.map(csvCell).join(',');
}
/** Serialises a table with CRLF line endings (RFC 4180) and a UTF-8 BOM, which
 *  is what makes Excel open non-ASCII labels correctly on Windows. */
export function serializeCsv(table, options = {}) {
    const lines = [csvRow(table.headers), ...table.rows.map(csvRow)];
    const body = lines.join('\r\n');
    return options.bom === false ? body : `﻿${body}`;
}
/**
 * Minimal RFC 4180 parser for CSV upload. Handles quoted fields containing
 * commas, quotes and newlines; does not attempt delimiter sniffing or type
 * inference beyond what the caller asks for.
 */
export function parseCsv(text) {
    const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    let i = 0;
    const endField = () => {
        row.push(field);
        field = '';
    };
    const endRow = () => {
        endField();
        rows.push(row);
        row = [];
    };
    while (i < input.length) {
        const char = input[i];
        if (inQuotes) {
            if (char === '"') {
                if (input[i + 1] === '"') {
                    field += '"';
                    i += 2;
                    continue;
                }
                inQuotes = false;
                i += 1;
                continue;
            }
            field += char;
            i += 1;
            continue;
        }
        if (char === '"' && field.length === 0) {
            inQuotes = true;
            i += 1;
            continue;
        }
        if (char === ',') {
            endField();
            i += 1;
            continue;
        }
        if (char === '\r') {
            if (input[i + 1] === '\n')
                i += 1;
            endRow();
            i += 1;
            continue;
        }
        if (char === '\n') {
            endRow();
            i += 1;
            continue;
        }
        field += char;
        i += 1;
    }
    if (field.length > 0 || row.length > 0)
        endRow();
    // A file ending in a line terminator can leave one phantom record behind:
    // the terminator that ends the last real row is consumed by `endRow`, so it
    // never reaches here — but a *second*, trailing terminator (or a lone
    // terminator with nothing before it) starts a fresh row containing exactly
    // one empty field, ['']. That is never real data; drop it. A genuine final
    // record such as `,` (`['', '']`) or `,,` must be kept whether or not a
    // newline follows it — it only conflates with the phantom when it also
    // happens to be exactly one empty field, which a real multi-column CSV row
    // never is.
    const last = rows[rows.length - 1];
    const endsInLineBreak = /[\r\n]$/.test(input);
    if (endsInLineBreak && last !== undefined && last.length === 1 && last[0] === '') {
        rows.pop();
    }
    return rows;
}
/**
 * Shared CSV value rules (docs/architecture/contracts.md §1): the regexes and word sets
 * that decide whether a raw cell looks like an integer, a number or a
 * boolean, plus the pure normalisation half of the column-id rule. Both
 * `csvToRows` and the server's upload inference use these, so there is one
 * definition of each.
 */
export const INTEGER_RE = /^[+-]?\d+$/;
export const NUMBER_RE = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;
export const TRUE_WORDS = new Set(['true', 'yes']);
export const FALSE_WORDS = new Set(['false', 'no']);
const COLUMN_ID_RE = /^[a-z][a-z0-9_]*$/;
/** The pure normalisation half of the browser's column-id rule: NFKD,
 *  snake_case, forced to start with a letter, at most 60 chars. No suffixing. */
export function columnIdBase(header) {
    let base = header
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    if (base.length === 0 || !/^[a-z]/.test(base))
        base = `column_${base}`.replace(/_+$/, '');
    if (!COLUMN_ID_RE.test(base))
        base = 'column';
    return base.slice(0, 60).replace(/_+$/, '') || 'column';
}
//# sourceMappingURL=csv.js.map